/**
 * One campaign request: where it stands and what comes next, its decision, its terms and brief,
 * with the actions open to the viewer. The brand can withdraw it, after confirming, and send a
 * request that ended without a campaign again. An admin can accept it into a manifest or reject
 * it, each with a note for the brand. An accepted request shows the deposit (see `Deposit.tsx`),
 * and a funded one its campaign, with what the brand can do with its budget.
 *
 * Only the brand changes the terms, and the admin team reviews every change (D25): the brand
 * edits a request waiting for review, and asks for a change of an accepted or lapsed one. A change
 * waiting for review shows what it changes, for the brand to withdraw and for an admin to accept
 * or reject. Decided changes stay listed with their outcome.
 *
 * Until it is funded, the brand can move the request to another wallet (D26). The wallet it is
 * offered to sees the request here, with its terms and brief, and takes it over or declines.
 */

import { ArrowLeft, Check } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useSession } from "../../auth/session.js";
import { refundAfterSeconds, useEscrowCampaign } from "../../campaigns/BrandFunds.js";
import { BriefCard, privateAssetUrl } from "../../campaigns/BriefCard.js";
import { campaignPath } from "../../campaigns/campaigns.js";
import { termDifferences } from "../../campaigns/changes.js";
import { isAwaited, useSentDeposit } from "../../campaigns/deposit.js";
import {
  type Campaign,
  type CampaignRequest,
  pendingChange,
  pendingTransfer,
  type RequestChange,
  type RequestTransfer,
  useAcceptChange,
  useAcceptedTokens,
  useAcceptRequest,
  useCancelMove,
  useDeclineRequest,
  useOfferRequest,
  useRejectChange,
  useRejectRequest,
  useRequest,
  useTakeOverRequest,
  useWithdrawChange,
  useWithdrawRequest,
} from "../../campaigns/requests.js";
import { StatusBadge } from "../../campaigns/StatusBadge.js";
import { AddressLink, shortHex } from "../../components/ChainLinks.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import { RequireWallet } from "../../components/RequireWallet.js";
import {
  Badge,
  type BadgeTone,
  ErrorMessage,
  Fact,
  Loading,
  Notice,
  PageHeader,
  Section,
  styles,
} from "../../components/ui.js";
import { useAppConfig } from "../../config.js";
import { formatAmount, formatCount, formatUtc, formatWindow } from "../../format.js";
import { DepositSection, UnmatchedDeposits } from "./Deposit.js";
import { platformLabel } from "./RequestList.js";

/** The brand's view of its request. */
export function BrandRequestDetail() {
  return (
    <RequireWallet reason="Sign in with the wallet you used for this request.">
      <RequestDetail view="brand" />
    </RequireWallet>
  );
}

/** The admin team's view of a request. */
export function AdminRequestDetail() {
  return (
    <RequireAdmin>
      <RequestDetail view="admin" />
    </RequireAdmin>
  );
}

function RequestDetail({ view }: { view: "brand" | "admin" }) {
  const id = Number(useParams().id);
  const request = useRequest(id);
  const session = useSession();
  const wallet = session.data?.wallet.toLowerCase();
  // Only admins see other wallets' requests. The brand's view speaks to the brand, so theirs. A
  // wallet a request is offered to sees it too, to take it over or decline (D26).
  const ownRequest = request.data?.brand.toLowerCase() === wallet;
  const offer = request.data && pendingTransfer(request.data);
  const offeredHere = offer !== undefined && offer.to.toLowerCase() === wallet;
  const back =
    view === "brand"
      ? { to: "/campaigns/requests", label: "Campaign requests" }
      : { to: "/admin/campaign-requests", label: "All requests" };

  // The admin pages' frame comes from the admin layout.
  return (
    <div className={view === "brand" ? styles.narrowPage : "max-w-4xl"}>
      <Link
        to={back.to}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
      >
        <ArrowLeft aria-hidden className="size-4" />
        {back.label}
      </Link>
      <div className="mt-4">
        {request.isPending && <Loading />}
        {request.isError && <ErrorMessage error={request.error} />}
        {request.data &&
          (view === "brand" && !ownRequest && offeredHere && offer ? (
            <OfferedRequest request={request.data} transfer={offer} />
          ) : view === "brand" && !ownRequest ? (
            <Notice tone="info" title={`Campaign request ${request.data.id}`}>
              This request belongs to another wallet.{" "}
              <Link to={`/admin/campaign-requests/${request.data.id}`} className={styles.link}>
                Open it in the admin pages
              </Link>
            </Notice>
          ) : (
            <RequestView request={request.data} view={view} />
          ))}
      </div>
    </div>
  );
}

function RequestView({ request, view }: { request: CampaignRequest; view: "brand" | "admin" }) {
  const { brief } = request;
  return (
    <>
      <PageHeader eyebrow={`Campaign request ${request.id}`} title={brief.title}>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge request={request} />
          <span className="text-sm">Submitted {formatUtc(request.createdAt)}</span>
        </div>
      </PageHeader>

      <RequestProgress request={request} />
      {view === "brand" && <NextStep request={request} />}
      <Decision request={request} />
      <Moves request={request} view={view} />
      <Changes request={request} view={view} />

      <DepositSection request={request} view={view} />
      <UnmatchedDeposits request={request} />

      <Terms request={request} />
      <BriefCard brief={brief} assetUrl={privateAssetUrl} />

      {view === "brand" && <BrandActions request={request} />}
      {view === "admin" && <AdminActions request={request} />}
    </>
  );
}

function Terms({ request }: { request: CampaignRequest }) {
  const { chainId } = useAppConfig();
  return (
    <Section title="Terms" className="mt-6">
      <dl className="grid gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
        <Fact label="Brand">{request.brandName}</Fact>
        <Fact label="Contact">{request.contactEmail}</Fact>
        <Fact label="Wallet">
          <AddressLink chainId={chainId} address={request.brand} />
        </Fact>
        <Fact label="Platform">{platformLabel(request)}</Fact>
        <Fact label="Budget">{formatAmount(request.budget, request.token)}</Fact>
        <Fact label="Target">
          {formatCount(request.target)} {request.metric.unit}
        </Fact>
        <Fact label="Creator threshold">
          {formatCount(request.threshold)} {request.metric.unit}
        </Fact>
        <Fact label="Submissions per account">{request.submissionLimit}</Fact>
        <Fact label="Window">{formatWindow(request.platform, request.startAt, request.endAt)}</Fact>
      </dl>
    </Section>
  );
}

/**
 * A request another wallet offers the signed-in one (D26): what taking it over means, the
 * request's terms and brief, and the choice.
 */
function OfferedRequest({
  request,
  transfer,
}: {
  request: CampaignRequest;
  transfer: RequestTransfer;
}) {
  const navigate = useNavigate();
  const takeOver = useTakeOverRequest();
  const decline = useDeclineRequest();
  const busy = takeOver.isPending || decline.isPending;
  return (
    <>
      <PageHeader eyebrow={`Campaign request ${request.id}`} title={request.brief.title}>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge request={request} />
          <span className="text-sm">Submitted {formatUtc(request.createdAt)}</span>
        </div>
      </PageHeader>
      <Notice tone="info" title="Offered to this wallet" className="mt-6">
        <p>
          The wallet{" "}
          <span className="font-mono text-xs" title={transfer.from}>
            {shortHex(transfer.from)}
          </span>{" "}
          offered you this request on {formatUtc(transfer.createdAt)}. Taking it over makes this
          wallet the request's brand wallet.{" "}
          {request.status === "submitted"
            ? "Once Morcast accepts the request, this wallet deposits the budget"
            : "This wallet deposits the budget"}
          , and the escrow returns any unspent part to it. The other wallet no longer sees the
          request.
        </p>
        <div className="mt-3 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => takeOver.mutate({ id: request.id, transferId: transfer.id })}
            className={styles.primaryButton}
          >
            {takeOver.isPending ? "Taking it over…" : "Take over the request"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              decline.mutate(
                { id: request.id, transferId: transfer.id },
                { onSuccess: () => navigate("/campaigns/requests") },
              )
            }
            className={styles.secondaryButton}
          >
            Decline
          </button>
        </div>
        {takeOver.isError && <ErrorMessage error={takeOver.error} />}
        {decline.isError && <ErrorMessage error={decline.error} />}
      </Notice>
      <Terms request={request} />
      <BriefCard brief={request.brief} assetUrl={privateAssetUrl} />
    </>
  );
}

/** The steps of a request that goes ahead, as far as they are known. */
const PROGRESS = ["Sent", "Accepted", "Deposited", "Running", "Settled"] as const;

/**
 * How far a request has come: sent, accepted, deposited, running, settled. Requests that stopped
 * (rejected, withdrawn, lapsed, cancelled or refunded) show no progress. Their decision or deposit
 * says why.
 */
function RequestProgress({ request }: { request: CampaignRequest }) {
  const done = progressOf(request);
  if (done === null) {
    return null;
  }
  return (
    <ol aria-label="Progress" className="mt-8 grid grid-cols-5 gap-2">
      {PROGRESS.map((label, index) => {
        const state = index < done ? "done" : index === done ? "current" : "next";
        return (
          <li key={label} className="min-w-0">
            <div
              className={`h-1.5 rounded-full ${
                state === "done"
                  ? "bg-brand-600 dark:bg-brand-400"
                  : state === "current"
                    ? "bg-brand-200 dark:bg-brand-400/35"
                    : "bg-zinc-200 dark:bg-white/[0.08]"
              }`}
            />
            <p
              className={`mt-2 flex items-center gap-1 text-xs font-medium ${
                state === "next" ? "text-zinc-400" : ""
              }`}
            >
              {state === "done" && (
                <Check
                  aria-hidden
                  className="size-3.5 shrink-0 text-brand-600 dark:text-brand-400"
                />
              )}
              <span className="truncate">{label}</span>
              <span className="sr-only">
                {state === "done" ? " (done)" : state === "current" ? " (current)" : ""}
              </span>
            </p>
          </li>
        );
      })}
    </ol>
  );
}

/** How many of the progress steps are done, or null for a request that stopped. */
function progressOf(request: CampaignRequest, now = Date.now()): number | null {
  switch (request.status) {
    case "submitted":
      return 1;
    case "accepted":
      return 2;
    case "funded": {
      const status = request.campaign?.status ?? "funded";
      if (status === "settled") {
        return 5;
      }
      if (status !== "funded") {
        return null;
      }
      return now < Date.parse(request.startAt) ? 3 : 4;
    }
    default:
      return null;
  }
}

/** Where the brand sends a request again: the form, filled in with this request's terms. */
function sendAgainPath(request: Pick<CampaignRequest, "id">): string {
  return `/campaigns/new?from=${request.id}`;
}

/** Where the brand edits a request waiting for review. */
function editPath(request: Pick<CampaignRequest, "id">): string {
  return `/campaigns/requests/${request.id}/edit`;
}

/** Where the brand asks for a change of an accepted or lapsed request. */
function changePath(request: Pick<CampaignRequest, "id">): string {
  return `/campaigns/requests/${request.id}/change`;
}

/** The brand's next step, with the page it happens on. */
function NextStep({ request }: { request: CampaignRequest }) {
  const now = Date.now();
  const sendAgain = (label: string) => (
    <Link to={sendAgainPath(request)} className={styles.link}>
      {label}
    </Link>
  );
  const move = pendingTransfer(request);
  if (move) {
    return (
      <Step>
        Waiting for the wallet you chose. It takes the request over once its holder signs in to
        Morcast and opens Campaign requests.
      </Step>
    );
  }
  switch (request.status) {
    case "submitted":
      return (
        <Step>
          Morcast is reviewing your request, and the decision appears here. Until the campaign is
          funded, you can still withdraw it.
        </Step>
      );
    case "accepted":
      if (pendingChange(request)) {
        return (
          <Step>
            Morcast is reviewing the change you asked for. Until it is decided, the accepted terms
            stay in force. Deposit once the change is decided, or withdraw it to deposit as it is.
          </Step>
        );
      }
      if (request.deposit && now < Date.parse(request.deposit.depositBefore)) {
        return (
          <Step tone="success">
            Your request is accepted. Next, deposit the budget from this wallet.{" "}
            <a href="#deposit" className={styles.link}>
              Go to the deposit
            </a>
            .
          </Step>
        );
      }
      return null;
    case "rejected":
      return (
        <Step>
          Morcast rejected this request, and its note is below. You can change the request and send
          it again. {sendAgain("Send a revised request")}
        </Step>
      );
    case "withdrawn":
      return <Step>You withdrew this request. {sendAgain("Send it again")}</Step>;
    case "lapsed":
      return pendingChange(request) ? (
        <Step>
          Morcast is reviewing the new dates you asked for. Once the change is accepted, you deposit
          before the new start.
        </Step>
      ) : (
        <Step>
          No deposit arrived before the start, so the request lapsed. To run the campaign, ask for
          new dates.{" "}
          <Link to={changePath(request)} className={styles.link}>
            Ask for new dates
          </Link>
        </Step>
      );
    case "funded":
      return request.campaign ? (
        <FundedStep request={request} campaign={request.campaign} sendAgain={sendAgain} />
      ) : null;
  }
}

/** The next step of a funded request, by its campaign's status in the escrow. */
function FundedStep({
  request,
  campaign,
  sendAgain,
}: {
  request: CampaignRequest;
  campaign: Campaign;
  sendAgain: (label: string) => ReactNode;
}) {
  const now = Date.now();
  const campaignLink = (
    <Link to={campaignPath(campaign)} className={styles.link}>
      Open the campaign page
    </Link>
  );
  const toDeposit = (
    <a href="#deposit" className={styles.link}>
      Go to the deposit
    </a>
  );
  switch (campaign.status) {
    case "funded":
      if (now < Date.parse(request.startAt)) {
        return (
          <Step tone="success">
            Your campaign is funded and starts {formatUtc(request.startAt)}. Creators can already
            find it. {campaignLink}
          </Step>
        );
      }
      if (now < Date.parse(request.endAt)) {
        return (
          <Step tone="success">
            Your campaign is running until {formatUtc(request.endAt)}. {campaignLink}
          </Step>
        );
      }
      if (now >= Date.parse(request.endAt) + refundAfterSeconds() * 1000) {
        return (
          <Step>
            Your campaign was not settled by Day 10, so the whole budget is yours to withdraw.{" "}
            {toDeposit}.
          </Step>
        );
      }
      return (
        <Step tone="success">
          Your campaign has ended. Morcast is measuring and checking the results, then settles it.
          You only pay for what is recognized. {campaignLink}
        </Step>
      );
    case "settled":
      return <SettledStep request={request} campaign={campaign} campaignLink={campaignLink} />;
    case "cancelled":
      return (
        <Step>
          You cancelled the campaign before its start, and the escrow returned the budget to your
          wallet. {sendAgain("Send the request again")}
        </Step>
      );
    case "refunded":
      return <Step>The escrow returned the whole budget to your wallet.</Step>;
  }
}

/**
 * A settled campaign. Its result, and the part of the budget it did not spend while the brand has
 * not withdrawn it, as the escrow records it.
 */
function SettledStep({
  request,
  campaign,
  campaignLink,
}: {
  request: CampaignRequest;
  campaign: Campaign;
  campaignLink: ReactNode;
}) {
  const onChain = useEscrowCampaign(campaign);
  const refund =
    onChain.data && onChain.data.refund > 0n && !onChain.data.refundPaid
      ? onChain.data.refund
      : null;
  return (
    <Step tone="success">
      Your campaign is settled, and its result is on the campaign page.{" "}
      {refund !== null && (
        <>
          Withdraw the {formatAmount(refund.toString(), request.token)} it did not spend from the{" "}
          <a href="#deposit" className={styles.link}>
            deposit section
          </a>
          .{" "}
        </>
      )}
      {campaignLink}
    </Step>
  );
}

function Step({ tone = "info", children }: { tone?: "info" | "success"; children: ReactNode }) {
  return (
    <Notice tone={tone} title="Next step" className="mt-6">
      {children}
    </Notice>
  );
}

/** The admin team's decision and its note, coloured by the outcome. */
function Decision({ request }: { request: CampaignRequest }) {
  const { decision } = request;
  if (!decision) {
    return null;
  }
  const rejected = request.status === "rejected";
  return (
    <Notice
      tone={rejected ? "error" : "info"}
      title={`${rejected ? "Rejected" : "Accepted"} ${formatUtc(decision.at)}`}
      className="mt-4"
    >
      {decision.note ?? (rejected ? "No note." : "No note from Morcast.")}
    </Notice>
  );
}

/**
 * The brand edits a request waiting for review, asks for a change of an accepted or lapsed one
 * while none waits, and withdraws a request until it is funded, after confirming. None of this
 * while a deposit this browser sent is on its way. That deposit would then fund nothing.
 */
function BrandActions({ request }: { request: CampaignRequest }) {
  const withdraw = useWithdrawRequest();
  const sent = useSentDeposit(request.id);
  const [confirming, setConfirming] = useState(false);
  const changeable =
    (request.status === "accepted" || request.status === "lapsed") && !pendingChange(request);
  // Until it is funded, a request can move to another wallet, one move at a time (D26).
  const movable =
    (request.status === "submitted" ||
      request.status === "accepted" ||
      request.status === "lapsed") &&
    !pendingTransfer(request);
  if (request.status !== "submitted" && request.status !== "accepted" && !changeable && !movable) {
    return null;
  }
  if (request.status === "accepted" && isAwaited(sent)) {
    return (
      <p className="mt-6 text-sm text-zinc-600 dark:text-zinc-400">
        Your deposit is on its way, so the request can no longer be withdrawn or changed.
      </p>
    );
  }
  return (
    <div className="mt-6 flex flex-wrap items-start gap-3">
      {request.status === "submitted" && (
        <Link to={editPath(request)} className={styles.secondaryButton}>
          Edit request
        </Link>
      )}
      {changeable && (
        <Link to={changePath(request)} className={styles.secondaryButton}>
          {request.status === "lapsed" ? "Ask for new dates" : "Ask for a change"}
        </Link>
      )}
      {movable && <MoveRequest request={request} />}
      {request.status !== "lapsed" && (
        <button
          type="button"
          disabled={withdraw.isPending}
          onClick={() => setConfirming(true)}
          className={styles.dangerButton}
        >
          {withdraw.isPending ? "Withdrawing…" : "Withdraw request"}
        </button>
      )}
      <ConfirmDialog
        open={confirming}
        title="Withdraw this request?"
        confirmLabel="Withdraw request"
        cancelLabel="Keep the request"
        onConfirm={() => withdraw.mutate(request.id)}
        onClose={() => setConfirming(false)}
      >
        {request.status === "accepted" ? (
          <p>
            Its deposit parameters stop working. A deposit made after this funds nothing, and
            Morcast voids it, which returns it to your wallet. To run the campaign later, send a new
            request.
          </p>
        ) : (
          <p>Morcast stops reviewing it. To run the campaign later, send a new request.</p>
        )}
      </ConfirmDialog>
      {withdraw.isError && (
        <div className="basis-full">
          <ErrorMessage error={withdraw.error} />
        </div>
      )}
    </div>
  );
}

/** The admin team decides a request waiting for review: accept it or reject it. */
function AdminActions({ request }: { request: CampaignRequest }) {
  if (request.status !== "submitted") {
    return null;
  }
  return (
    <div className="mt-6 grid gap-6 md:grid-cols-2">
      <AcceptForm request={request} />
      <RejectForm request={request} />
    </div>
  );
}

/**
 * Accepting fixes the terms in a manifest that names the deployment receiving new campaigns. The
 * API checks the terms and the deployment again first, and lists every problem it finds.
 */
function AcceptForm({ request }: { request: CampaignRequest }) {
  const accept = useAcceptRequest();
  const [note, setNote] = useState("");

  function onAccept(event: FormEvent) {
    event.preventDefault();
    accept.mutate({ id: request.id, note: note.trim(), updatedAt: request.updatedAt });
  }

  return (
    <form onSubmit={onAccept} className={styles.card}>
      <h2 className="font-display font-semibold text-zinc-950 dark:text-white">Accept</h2>
      <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
        Fixes these terms in a manifest and gives the brand the deposit parameters.
      </p>
      <label className="mt-3 block text-sm font-medium">
        Note with the acceptance (optional)
        <textarea
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className={styles.input}
        />
      </label>
      <button type="submit" disabled={accept.isPending} className={`mt-4 ${styles.primaryButton}`}>
        {accept.isPending ? "Checking on-chain…" : "Accept request"}
      </button>
      {accept.isError && <ErrorMessage error={accept.error} />}
    </form>
  );
}

/** The shortest note a rejection takes, as the API counts it: without surrounding spaces. */
const MIN_REJECTION_NOTE = 5;

/** Rejecting needs a note, which the brand sees. */
function RejectForm({ request }: { request: CampaignRequest }) {
  const reject = useRejectRequest();
  const [note, setNote] = useState("");
  const ready = note.trim().length >= MIN_REJECTION_NOTE;
  const hintId = useId();

  function onReject(event: FormEvent) {
    event.preventDefault();
    if (ready) {
      reject.mutate({ id: request.id, note: note.trim(), updatedAt: request.updatedAt });
    }
  }

  return (
    <form onSubmit={onReject} noValidate className={styles.card}>
      <h2 className="font-display font-semibold text-zinc-950 dark:text-white">Reject</h2>
      <label className="mt-3 block text-sm font-medium">
        Note to the brand
        <textarea
          rows={3}
          value={note}
          aria-describedby={hintId}
          onChange={(event) => setNote(event.target.value)}
          className={styles.input}
        />
      </label>
      <p id={hintId} className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
        Say why in at least {MIN_REJECTION_NOTE} characters. The brand sees it.
      </p>
      <button
        type="submit"
        disabled={reject.isPending || !ready}
        className={`mt-4 ${styles.dangerButton}`}
      >
        Reject request
      </button>
      {reject.isError && <ErrorMessage error={reject.error} />}
    </form>
  );
}

/**
 * The request's changes (D25). The one waiting for review, with what it changes, for the brand to
 * withdraw and for an admin to decide. Then the earlier ones with their outcome, newest first.
 */
function Changes({ request, view }: { request: CampaignRequest; view: "brand" | "admin" }) {
  const pending = pendingChange(request);
  const closed = request.changes.filter((change) => change.status !== "pending");
  if (!pending && closed.length === 0) {
    return null;
  }
  return (
    <>
      {pending && <PendingChange request={request} change={pending} view={view} />}
      {closed.length > 0 && <ChangeHistory changes={closed} />}
    </>
  );
}

function PendingChange({
  request,
  change,
  view,
}: {
  request: CampaignRequest;
  change: RequestChange;
  view: "brand" | "admin";
}) {
  const tokens = useAcceptedTokens();
  const differences = termDifferences(request, change.terms, tokens.data ?? []);
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      className={`mt-6 ${styles.card} ring-amber-500/40 dark:ring-amber-400/30`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id={titleId}
          className="font-display text-lg font-semibold tracking-[-0.015em] text-zinc-950 dark:text-white"
        >
          {view === "brand" ? "Your change, waiting for review" : "Change to review"}
        </h2>
        <Badge tone="amber">Waiting for review</Badge>
      </div>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
        Asked {formatUtc(change.createdAt)}.{" "}
        {view === "brand"
          ? "Until Morcast decides it, the accepted terms stay in force."
          : "Accepting it fixes the new terms in a new manifest, and the brand deposits with them. Rejecting it keeps the request as it is."}
      </p>
      <p className="mt-4 text-sm font-medium text-zinc-900 dark:text-zinc-100">
        {view === "brand" ? "Your reason" : "The brand's reason"}
      </p>
      <p className="mt-1 text-sm whitespace-pre-line text-zinc-700 dark:text-zinc-300">
        {change.reason}
      </p>
      <Differences differences={differences} />
      {view === "brand" ? (
        <WithdrawChange request={request} change={change} />
      ) : (
        <ChangeDecision request={request} change={change} />
      )}
    </section>
  );
}

/** What a change changes, as a table of each field now and as asked for. */
function Differences({ differences }: { differences: ReturnType<typeof termDifferences> }) {
  if (differences.length === 0) {
    return (
      <p className="mt-4 text-sm text-zinc-600 dark:text-zinc-400">
        The change asks for the terms the request has now.
      </p>
    );
  }
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">What the change changes</caption>
        <thead className="text-zinc-500 dark:text-zinc-400">
          <tr>
            <th scope="col" className="py-2 pr-4 font-medium">
              Term
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Now
            </th>
            <th scope="col" className="py-2 font-medium">
              Asked for
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-900/[0.07] align-top dark:divide-white/10">
          {differences.map((difference) => (
            <tr key={difference.label}>
              <th scope="row" className="py-2 pr-4 font-medium text-zinc-900 dark:text-zinc-100">
                {difference.label}
              </th>
              <td className="py-2 pr-4 break-words text-zinc-500 line-through decoration-zinc-400/60 dark:text-zinc-400">
                {difference.from}
              </td>
              <td className="py-2 break-words text-zinc-900 dark:text-zinc-100">{difference.to}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The brand withdraws its change, for example to deposit with the accepted terms. */
function WithdrawChange({ request, change }: { request: CampaignRequest; change: RequestChange }) {
  const withdraw = useWithdrawChange();
  return (
    <div className="mt-4">
      <button
        type="button"
        disabled={withdraw.isPending}
        onClick={() => withdraw.mutate({ id: request.id, changeId: change.id })}
        className={styles.secondaryButton}
      >
        {withdraw.isPending ? "Withdrawing…" : "Withdraw the change"}
      </button>
      {withdraw.isError && <ErrorMessage error={withdraw.error} />}
    </div>
  );
}

/** An admin accepts the change into a new manifest, or rejects it with a note for the brand. */
function ChangeDecision({ request, change }: { request: CampaignRequest; change: RequestChange }) {
  const accept = useAcceptChange();
  const reject = useRejectChange();
  const [acceptNote, setAcceptNote] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const ready = rejectNote.trim().length >= MIN_REJECTION_NOTE;
  const hintId = useId();

  function onAccept(event: FormEvent) {
    event.preventDefault();
    accept.mutate({ id: request.id, changeId: change.id, note: acceptNote.trim() });
  }

  function onReject(event: FormEvent) {
    event.preventDefault();
    if (ready) {
      reject.mutate({ id: request.id, changeId: change.id, note: rejectNote.trim() });
    }
  }

  return (
    <div className="mt-6 grid gap-6 md:grid-cols-2">
      <form
        onSubmit={onAccept}
        className="rounded-xl p-4 ring-1 ring-zinc-900/[0.07] dark:ring-white/10"
      >
        <h3 className="font-display font-semibold text-zinc-950 dark:text-white">
          Accept the change
        </h3>
        <label className="mt-3 block text-sm font-medium">
          Note to the brand (optional)
          <textarea
            rows={2}
            value={acceptNote}
            onChange={(event) => setAcceptNote(event.target.value)}
            className={styles.input}
          />
        </label>
        <button
          type="submit"
          disabled={accept.isPending || reject.isPending}
          className={`mt-3 ${styles.primaryButton}`}
        >
          {accept.isPending ? "Checking on-chain…" : "Accept the change"}
        </button>
        {accept.isError && <ErrorMessage error={accept.error} />}
      </form>
      <form
        onSubmit={onReject}
        noValidate
        className="rounded-xl p-4 ring-1 ring-zinc-900/[0.07] dark:ring-white/10"
      >
        <h3 className="font-display font-semibold text-zinc-950 dark:text-white">
          Reject the change
        </h3>
        <label className="mt-3 block text-sm font-medium">
          Note to the brand
          <textarea
            rows={2}
            value={rejectNote}
            aria-describedby={hintId}
            onChange={(event) => setRejectNote(event.target.value)}
            className={styles.input}
          />
        </label>
        <p id={hintId} className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Say why in at least {MIN_REJECTION_NOTE} characters. The brand sees it.
        </p>
        <button
          type="submit"
          disabled={reject.isPending || accept.isPending || !ready}
          className={`mt-3 ${styles.dangerButton}`}
        >
          Reject the change
        </button>
        {reject.isError && <ErrorMessage error={reject.error} />}
      </form>
    </div>
  );
}

/** Each closed change's outcome, as a badge. */
const CHANGE_OUTCOMES: Record<
  Exclude<RequestChange["status"], "pending">,
  { label: string; tone: BadgeTone }
> = {
  accepted: { label: "Accepted", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  withdrawn: { label: "Withdrawn", tone: "grey" },
  superseded: { label: "Superseded", tone: "grey" },
};

/** The request's earlier changes, newest first, each with its outcome and Morcast's note. */
function ChangeHistory({ changes }: { changes: RequestChange[] }) {
  return (
    <Section title="Earlier changes" className="mt-6">
      <ul className="divide-y divide-zinc-900/[0.07] text-sm dark:divide-white/10">
        {changes.map((change) => {
          const outcome = CHANGE_OUTCOMES[change.status as keyof typeof CHANGE_OUTCOMES];
          return (
            <li key={change.id} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={outcome.tone}>{outcome.label}</Badge>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                  Asked {formatUtc(change.createdAt)}
                  {change.closedAt && `, closed ${formatUtc(change.closedAt)}`}
                </span>
              </div>
              <p className="mt-1.5 whitespace-pre-line text-zinc-700 dark:text-zinc-300">
                {change.reason}
              </p>
              {change.decisionNote && (
                <p className="mt-1 text-zinc-600 dark:text-zinc-400">
                  <span className="font-medium text-zinc-800 dark:text-zinc-200">
                    Morcast's note:
                  </span>{" "}
                  {change.decisionNote}
                </p>
              )}
              {change.status === "superseded" && (
                <p className="mt-1 text-zinc-600 dark:text-zinc-400">
                  A deposit with the accepted terms funded the request before it was decided.
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** A wallet address as the move dialog takes it: 0x and 40 hexadecimal digits. */
const WALLET_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

/**
 * The brand offers its request, not yet funded, to another wallet (D26), such as a company wallet
 * that should make the deposit. The request moves once that wallet takes it over.
 */
function MoveRequest({ request }: { request: CampaignRequest }) {
  const offer = useOfferRequest();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const fieldId = useId();
  const valid = WALLET_ADDRESS.test(to.trim());
  const same = to.trim().toLowerCase() === request.brand.toLowerCase();
  return (
    <>
      <button
        type="button"
        disabled={offer.isPending}
        onClick={() => setOpen(true)}
        className={styles.secondaryButton}
      >
        Move to another wallet
      </button>
      <ConfirmDialog
        open={open}
        title="Move this request to another wallet?"
        confirmLabel="Offer the request"
        cancelLabel="Keep it here"
        tone="primary"
        confirmDisabled={!valid || same}
        onConfirm={() => offer.mutate({ id: request.id, to: to.trim() })}
        onClose={() => {
          setOpen(false);
          setTo("");
        }}
      >
        <p>
          The other wallet, such as your company's, takes the request over once it signs in to
          Morcast. It then deposits the budget and gets back any unspent part, and this wallet no
          longer sees the request. The terms stay as they are.
        </p>
        <label htmlFor={fieldId} className="block font-medium text-zinc-800 dark:text-zinc-200">
          The other wallet's address
        </label>
        <input
          id={fieldId}
          value={to}
          onChange={(event) => setTo(event.target.value)}
          placeholder="0x…"
          spellCheck={false}
          autoComplete="off"
          className={`font-mono ${styles.input}`}
        />
        {to.trim() !== "" && !valid && (
          <p className="text-xs text-red-700 dark:text-red-400">
            A wallet address is 0x and 40 letters and digits.
          </p>
        )}
        {same && (
          <p className="text-xs text-red-700 dark:text-red-400">
            The request already belongs to this wallet.
          </p>
        )}
      </ConfirmDialog>
      {offer.isError && (
        <div className="basis-full">
          <ErrorMessage error={offer.error} />
        </div>
      )}
    </>
  );
}

/** Each closed move's outcome, as a badge. */
const MOVE_OUTCOMES: Record<
  Exclude<RequestTransfer["status"], "pending">,
  { label: string; tone: BadgeTone }
> = {
  accepted: { label: "Moved", tone: "green" },
  declined: { label: "Declined", tone: "grey" },
  cancelled: { label: "Cancelled", tone: "grey" },
  superseded: { label: "Superseded", tone: "grey" },
};

/**
 * The request's moves to other wallets (D26). The one waiting to be taken over, which the brand
 * can cancel, and the earlier ones with their outcome, newest first.
 */
function Moves({ request, view }: { request: CampaignRequest; view: "brand" | "admin" }) {
  const cancel = useCancelMove();
  const pending = pendingTransfer(request);
  const closed = request.transfers.filter((transfer) => transfer.status !== "pending");
  if (!pending && closed.length === 0) {
    return null;
  }
  return (
    <>
      {pending && (
        <Notice tone="info" title="Moving to another wallet" className="mt-6">
          <p>
            Offered to{" "}
            <span className="font-mono text-xs break-all" title={pending.to}>
              {pending.to}
            </span>{" "}
            on {formatUtc(pending.createdAt)}.{" "}
            {view === "brand"
              ? "Until that wallet takes it over, the request stays with this one."
              : "The request moves once that wallet takes it over."}
          </p>
          {view === "brand" && (
            <>
              <button
                type="button"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate({ id: request.id, transferId: pending.id })}
                className={`mt-3 ${styles.secondaryButton}`}
              >
                {cancel.isPending ? "Cancelling…" : "Cancel the move"}
              </button>
              {cancel.isError && <ErrorMessage error={cancel.error} />}
            </>
          )}
        </Notice>
      )}
      {closed.length > 0 && (
        <Section title="Wallet moves" className="mt-6">
          <ul className="divide-y divide-zinc-900/[0.07] text-sm dark:divide-white/10">
            {closed.map((transfer) => {
              const outcome = MOVE_OUTCOMES[transfer.status as keyof typeof MOVE_OUTCOMES];
              return (
                <li
                  key={transfer.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 first:pt-0 last:pb-0"
                >
                  <Badge tone={outcome.tone}>{outcome.label}</Badge>
                  <span className="text-zinc-700 dark:text-zinc-300">
                    From{" "}
                    <span className="font-mono text-xs" title={transfer.from}>
                      {shortHex(transfer.from)}
                    </span>{" "}
                    to{" "}
                    <span className="font-mono text-xs" title={transfer.to}>
                      {shortHex(transfer.to)}
                    </span>
                  </span>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">
                    {formatUtc(transfer.closedAt ?? transfer.createdAt)}
                  </span>
                </li>
              );
            })}
          </ul>
        </Section>
      )}
    </>
  );
}
